<template>
    <div>
        <div class="input-group">
            <search-filter
                v-if="useFilters"
                ref="filterSelector"
                v-bind:options="filterOptions"
                v-bind:initial-filters="initialFilters"
                v-bind:no-filter-text="noFilterText"
                class="form-control"
                v-on:filter-update="onFilterUpdate" />
            <input
                ref="input"
                v-model.trim="searchTerm"
                v-bind:disabled="disabled"
                v-bind:maxlength="maxlength"
                v-bind:aria-activedescendant="ariaActiveDescendantId"
                v-bind:aria-controls="ariaControls"
                v-bind:role="role"
                v-bind:placeholder="placeholder"
                data-test-role="search-input"
                aria-label="search term"
                class="form-control"
                type="text"
                v-on:keyup.enter="search(searchCallback)"
                v-on="{ ...externalNativeHandlers }" />
            <template v-if="!hideInputGroupAppend">
                <button
                    v-bind:disabled="disabled"
                    data-test-role="search-button"
                    class="btn border btn-secondary input-group-text"
                    type="button"
                    aria-label="search"
                    v-on:click="search(searchCallback)">
                    <pc-bs-icon name="search" />
                </button>
                <button
                    v-if="hasTerm || (useFilters && hasFilters)"
                    v-bind:disabled="disabled"
                    data-test-role="search-clear-button"
                    class="btn border btn-secondary input-group-text"
                    type="button"
                    aria-label="clear search"
                    v-on:click="clear"
                    v-on:keyup.enter.stop="clear">
                    <pc-bs-icon name="x-lg" />
                </button>
            </template>
        </div>
    </div>
</template>

<script>
    import searchFilter from "./searchFilter.vue";
    import { ref } from "vue";
    import { useSearch } from "./search.js";

    export default {
        components: {
            searchFilter
        },
        props: {
            initialSearch: { type: String, required: false, default: null },
            placeholder: { type: String, required: false, default: null },
            disabled: { type: Boolean, required: false, default: false },
            maxlength: { type: Number, required: false, default: null },
            filterOptions: { type: Array, required: false, default: () => [] },
            initialFilters: { type: Array, required: false, default: () => [] },
            noFilterText: { type: String, required: false, default: null },
            hideInputGroupAppend: { type: Boolean, required: false, default: false },
            externalNativeHandlers: { type: Object, required: false, default: null },
            role: { type: String, required: false, default: null },
            ariaControls: { type: String, required: false, default: null },
            ariaActiveDescendantId: { type: String, required: false, default: null },
            dynamic: {
                type: Object,
                required: false,
                default: function () {
                    return { debounce: 250, reactive: true };
                }
            }
        },
        emits: ["search-filters", "search"],
        setup(props, context) {
            const filters = ref(props.initialFilters || []);
            const hasFilters = ref(props.initialFilters.length > 0);

            const searchTermInternal = ref(props.initialSearch || "");

            function searchCallback() {
                hasFilters.value = filters.value.length > 0;
                if (hasFilters.value) {
                    context.emit("search-filters", searchTermInternal.value, filters.value);
                } else {
                    context.emit("search", searchTermInternal.value);
                }
            }

            return {
                ...useSearch(context, searchTermInternal, props.dynamic, props.disabled, searchCallback),
                searchTermInternal,
                filters,
                hasFilters,
                searchCallback
            };
        },
        computed: {
            searchTerm: {
                get() {
                    return this.searchTermInternal;
                },
                set(value) {
                    this.setSearchTerm(value, false);
                }
            },
            useFilters() {
                return this.filterOptions.length > 0;
            }
        },
        methods: {
            clear({ skipEmit }) {
                this.setSearchTerm("", skipEmit);
                if (this.useFilters) {
                    this.filters = [];
                    this.$refs.filterSelector.updateFilterSelected(null);
                }
                if (!skipEmit) {
                    this.search(this.searchCallback);
                }
                this.focusIn();
            },
            onFilterUpdate(newFilters) {
                this.filters = newFilters;
                if (this.hasTerm) {
                    this.search(this.searchCallback);
                }
            },
            focusIn() {
                this.$refs.input?.focus();
            },
            focusInAndSelect() {
                this.$refs.input?.focus();
                this.$refs.input?.select();
            }
        }
    };
</script>
